import { inject, Injectable } from '@angular/core';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { ExpenseService } from '@core/services/expense.service';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { ApiResponse } from '@core/types';
import { Expense, ExpenseDetail, ExpenseCreate, ExpenseUpdate } from '@core/types/finance.type';
import { QUERY_KEYS } from '@core/constans';
import { environment } from '@environments/environment';
import { lastValueFrom } from 'rxjs';
import { profileStore } from '@core/store';

@Injectable({ providedIn: 'root' })
export class ExpenseQuery {
  private readonly expenseService = inject(ExpenseService);
  private readonly queryClient = inject(QueryClient);
  private readonly profileStore = inject(profileStore);

  useExpensesGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      const currentConfig = config();
      return {
        queryKey: [...QUERY_KEYS.expenses.grid(currentConfig), condominiumId],
        queryFn: () => lastValueFrom(this.expenseService.getExpenses(currentConfig)),
        select: (data: ApiResponse<PaginationResult<Expense>>) => data.data,
        staleTime: environment.staleTime,
        enabled: !!condominiumId,
      };
    });
  }

  useExpenseDetail(expenseId: () => string | null) {
    return injectQuery(() => {
      const id = expenseId();
      return {
        queryKey: QUERY_KEYS.expenses.detail(id || ''),
        queryFn: () => lastValueFrom(this.expenseService.getExpense(id!)),
        select: (data: ApiResponse<ExpenseDetail>) => data.data,
        enabled: !!id,
      };
    });
  }

  useCreateExpense() {
    return injectMutation(() => ({
      mutationFn: (data: ExpenseCreate) =>
        lastValueFrom(this.expenseService.createExpense(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.transactions.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.budgets.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }

  useUpdateExpense() {
    return injectMutation(() => ({
      mutationFn: (data: ExpenseUpdate) =>
        lastValueFrom(this.expenseService.updateExpense(data)),
      onSuccess: (_result, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.all });
      },
    }));
  }

  useDeleteExpense() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.expenseService.deleteExpense(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.transactions.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }

  useMarkExpenseAsPaid() {
    return injectMutation(() => ({
      mutationFn: (params: { id: string; paidDate: string; reference?: string }) =>
        lastValueFrom(
          this.expenseService.markExpenseAsPaid(params.id, params.paidDate, params.reference)
        ),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.transactions.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.all });
      },
    }));
  }
}
